import React, { useState, useEffect, useMemo } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Button,
    Alert,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Divider,
    Snackbar
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Editor } from '@monaco-editor/react';
import { config } from '../../config/config';
import { collectionPointsService, SnapshotStructure, ConsentPayload } from '../../services/collectionpoints-service';
import { apiKeysService } from '../../services/apiKeys-service';

interface IntegrationsTabProps {
    collectionPoint: {
        CollectionPointId: number;
        CollectionPointIdentifier: string;
        Name: string;
        IsPublished: boolean;
    } | null;
}

const IntegrationsTab: React.FC<IntegrationsTabProps> = ({ collectionPoint }) => {
    const [snapshotJSON, setSnapshotJSON] = useState<string | null>(null);
    const [samplePayload, setSamplePayload] = useState<string>('');
    const [testResponse, setTestResponse] = useState<string>('');
    const [testRequestDetails, setTestRequestDetails] = useState<{
        url: string;
        method: string;
        headers: Record<string, string>;
        body: string;
    } | null>(null);
    const [testResponseDetails, setTestResponseDetails] = useState<{
        status: number;
        statusText: string;
        headers: Record<string, string>;
        body: string;
    } | null>(null);
    const [isLoadingSnapshot, setIsLoadingSnapshot] = useState(false);
    const [isSubmittingTest, setIsSubmittingTest] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showCopyNotification, setShowCopyNotification] = useState(false);
    const [showErrorNotification, setShowErrorNotification] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [selectedLanguage, setSelectedLanguage] = useState<string>('en');

    // Common Monaco Editor options
    const monacoOptions = useMemo(() => ({
        readOnly: true,
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        fontSize: 12,
        wordWrap: 'on' as const,
        lineNumbers: 'on' as const,
        folding: true,
        showFoldingControls: 'always' as const
    }), []);

    const editableMonacoOptions = useMemo(() => ({
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        fontSize: 12,
        wordWrap: 'on' as const,
        lineNumbers: 'on' as const,
        folding: true,
        showFoldingControls: 'always' as const
    }), []);

    // Load snapshot JSON when collection point is available
    useEffect(() => {
        const loadSnapshot = async () => {
            if (!collectionPoint?.CollectionPointIdentifier) {
                setSnapshotJSON(null);
                return;
            }

            setIsLoadingSnapshot(true);
            setError(null);

            try {
                const snapshotData: SnapshotStructure = await collectionPointsService.getCollectionPointSnapshot(
                    collectionPoint.CollectionPointIdentifier
                );
                
                setSnapshotJSON(JSON.stringify(snapshotData, null, 2));
            } catch (error: any) {
                console.error('Error loading snapshot:', error);
                
                // Handle specific error cases
                if (error.response?.status === 501) {
                    setError('Snapshot retrieval for published collection points is not yet implemented. Please try again later.');
                } else {
                    setError('Failed to load snapshot JSON. Please try again.');
                }
            } finally {
                setIsLoadingSnapshot(false);
            }
        };

        loadSnapshot();
    }, [collectionPoint?.CollectionPointIdentifier]);

    // Validate JSON format
    const validateJSON = (jsonString: string): boolean => {
        try {
            JSON.parse(jsonString);
            return true;
        } catch {
            return false;
        }
    };

    // Generate sample payload based on snapshot
    const generateSamplePayload = (snapshot: SnapshotStructure, languageCode = 'en') => {
        try {
            const payload: ConsentPayload = {
                collectionPointIdentifier: snapshot.collectionPoint.identifier,
                consentDate: new Date().toISOString(),
                dataPrincipal: {
                    identifier: "sample@example.com",
                    identifierType: "EMAIL"
                },
                purposes: {},
                dataElements: {},
                preferences: {},
                metadata: {
                    languageCode: languageCode, // Use the selected language
                    ipAddress: "192.168.1.100",
                    userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
                    source: "WEB"
                }
            };

            // Add sample purpose consents
            snapshot.purposes.forEach(purpose => {
                payload.purposes[purpose.key] = {
                    consented: true,
                    timestamp: new Date().toISOString()
                };
            });

            // Helper function to get language-specific sample data
            const getLanguageSpecificSampleData = (dataType: string, languageCode = 'en') => {
                const languageSamples: Record<string, Record<string, string>> = {
                    'en': {
                        'email': 'john.doe@example.com',
                        'phone': '+91-98765-43210',
                        'name': 'John Doe',
                        'first_name': 'John',
                        'last_name': 'Doe',
                        'address': '123 Main Street, Mumbai, Maharashtra 400001',
                        'identifier': 'ID123456789',
                        'preference': 'preferred',
                        'behavioral': 'active_user',
                        'technical': '192.168.1.100',
                        'financial': 'INR 50000',
                        'health': 'healthy'
                    },
                    'hi': {
                        'email': 'raj.kumar@example.com',
                        'phone': '+91-98765-43210',
                        'name': 'राज कुमार',
                        'first_name': 'राज',
                        'last_name': 'कुमार',
                        'address': '123 मुख्य सड़क, मुंबई, महाराष्ट्र 400001',
                        'identifier': 'ID123456789',
                        'preference': 'पसंदीदा',
                        'behavioral': 'सक्रिय_उपयोगकर्ता',
                        'technical': '192.168.1.100',
                        'financial': '₹50000',
                        'health': 'स्वस्थ'
                    },
                    'bn': {
                        'email': 'rahul.das@example.com',
                        'phone': '+91-98765-43210',
                        'name': 'রাহুল দাস',
                        'first_name': 'রাহুল',
                        'last_name': 'দাস',
                        'address': '১২৩ মূল সড়ক, মুম্বাই, মহারাষ্ট্র ৪০০০০১',
                        'identifier': 'ID123456789',
                        'preference': 'পছন্দসই',
                        'behavioral': 'সক্রিয়_ব্যবহারকারী',
                        'technical': '192.168.1.100',
                        'financial': '₹৫০০০০',
                        'health': 'সুস্থ'
                    },
                    'te': {
                        'email': 'venkat.rao@example.com',
                        'phone': '+91-98765-43210',
                        'name': 'వెంకట్ రావు',
                        'first_name': 'వెంకట్',
                        'last_name': 'రావు',
                        'address': '123 ప్రధాన వీధి, ముంబై, మహారాష్ట్ర 400001',
                        'identifier': 'ID123456789',
                        'preference': 'ఇష్టమైన',
                        'behavioral': 'క్రియాశీల_వినియోగదారు',
                        'technical': '192.168.1.100',
                        'financial': '₹50000',
                        'health': 'ఆరోగ్యవంతుడు'
                    },
                    'mr': {
                        'email': 'suresh.patil@example.com',
                        'phone': '+91-98765-43210',
                        'name': 'सुरेश पाटील',
                        'first_name': 'सुरेश',
                        'last_name': 'पाटील',
                        'address': '१२३ मुख्य रस्ता, मुंबई, महाराष्ट्र ४००००१',
                        'identifier': 'ID123456789',
                        'preference': 'आवडतो',
                        'behavioral': 'सक्रिय_वापरकर्ता',
                        'technical': '192.168.1.100',
                        'financial': '₹५००००',
                        'health': 'निरोगी'
                    }
                };

                const samples = languageSamples[languageCode] || languageSamples['en'];
                return samples[dataType.toLowerCase()] || samples['name'] || `Sample ${dataType} value`;
            };

            // Add sample data element values with language-specific data
            snapshot.dataElements.forEach(dataElement => {
                const currentLanguageCode = payload.metadata?.languageCode || languageCode;
                let sampleValue: string;
                
                // Handle specific data element names that might have translations
                if (dataElement.name.toLowerCase().includes('first name') || dataElement.name.toLowerCase().includes('first_name')) {
                    sampleValue = getLanguageSpecificSampleData('first_name', currentLanguageCode);
                } else if (dataElement.name.toLowerCase().includes('last name') || dataElement.name.toLowerCase().includes('last_name')) {
                    sampleValue = getLanguageSpecificSampleData('last_name', currentLanguageCode);
                } else {
                    sampleValue = getLanguageSpecificSampleData(dataElement.dataType, currentLanguageCode);
                }
                
                payload.dataElements[dataElement.key] = sampleValue;
            });

            // Add sample preference selections
            snapshot.preferences.forEach(preference => {
                if (preference.options && preference.options.length > 0) {
                    if (preference.allowMultipleSelection) {
                        payload.preferences[preference.key] = preference.options
                            .filter(opt => opt.isDefaultSelected)
                            .map(opt => opt.key);
                    } else {
                        const defaultOption = preference.options.find(opt => opt.isDefaultSelected);
                        payload.preferences[preference.key] = defaultOption ? defaultOption.key : preference.options[0].key;
                    }
                }
            });

            return JSON.stringify(payload, null, 2);
        } catch (error) {
            console.error('Error generating sample payload:', error);
            return JSON.stringify({
                collectionPointIdentifier: snapshot.collectionPoint.identifier,
                consentDate: new Date().toISOString(),
                dataPrincipal: {
                    identifier: "sample@example.com",
                    identifierType: "EMAIL"
                },
                purposes: {},
                dataElements: {},
                preferences: {},
                metadata: {
                    languageCode: "en",
                    ipAddress: "192.168.1.100",
                    userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
                    source: "WEB"
                }
            }, null, 2);
        }
    };

    // Update sample payload when snapshot or selected language changes
    useEffect(() => {
        if (snapshotJSON) {
            try {
                const snapshot = JSON.parse(snapshotJSON);
                setSamplePayload(generateSamplePayload(snapshot, selectedLanguage));
            } catch (error) {
                console.error('Error parsing snapshot JSON:', error);
                setSamplePayload('');
            }
        }
    }, [snapshotJSON, selectedLanguage]);

    const handleCopyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setShowCopyNotification(true);
        }).catch((error) => {
            console.error('Failed to copy to clipboard:', error);
            setErrorMessage('Failed to copy to clipboard. Please try again.');
            setShowErrorNotification(true);
        });
    };

    const handleTestSubmission = async () => {
        if (!collectionPoint?.CollectionPointIdentifier || !samplePayload) return;

        // Validate JSON format before submission
        if (!validateJSON(samplePayload)) {
            setErrorMessage('Invalid JSON format in payload. Please check the syntax.');
            setShowErrorNotification(true);
            return;
        }

        setIsSubmittingTest(true);
        setTestResponse('');
        setTestRequestDetails(null);
        setTestResponseDetails(null);
        setError(null);

        try {
            const payload: ConsentPayload = JSON.parse(samplePayload);
            
            // Get API key for request details
            const apiKeyResponse = await apiKeysService.getMyApiKey();
            const apiKey = apiKeyResponse.data.apiKey;
            
            // Prepare request details
            const requestUrl = `${config.api.baseUrl}/collectionpoints/${collectionPoint.CollectionPointIdentifier}/consent/sandbox`;
            const requestHeaders = {
                'Content-Type': 'application/json',
                'x-api-key': apiKey
            };
            
            setTestRequestDetails({
                url: requestUrl,
                method: 'POST',
                headers: requestHeaders,
                body: JSON.stringify(payload, null, 2)
            });
            
            const response = await collectionPointsService.submitSandboxConsent(
                collectionPoint.CollectionPointIdentifier,
                payload
            );

            setTestResponse(JSON.stringify(response, null, 2));
            
            // Set response details (we'll get these from the actual response)
            setTestResponseDetails({
                status: 200,
                statusText: 'OK',
                headers: {
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                    'Access-Control-Allow-Headers': 'Content-Type, Authorization, x-api-key'
                },
                body: JSON.stringify(response, null, 2)
            });
        } catch (error: any) {
            const errorMsg = error.message || 'Unknown error occurred';
            setError(`Test submission failed: ${errorMsg}`);
            
            // Set error response details
            if (error.response) {
                setTestResponseDetails({
                    status: error.response.status || 500,
                    statusText: error.response.statusText || 'Error',
                    headers: error.response.headers || {},
                    body: JSON.stringify(error.response.data || { error: errorMsg }, null, 2)
                });
            }
            
            // Show error notification for network/parsing errors
            if (error.name === 'SyntaxError') {
                setErrorMessage('Invalid JSON format in payload. Please check the syntax.');
            } else if (error.name === 'TypeError') {
                setErrorMessage('Network error. Please check your connection and try again.');
            } else {
                setErrorMessage(`Test submission failed: ${errorMsg}`);
            }
            setShowErrorNotification(true);
        } finally {
            setIsSubmittingTest(false);
        }
    };

    const renderCollapsibleSnapshot = () => {
        if (!snapshotJSON) return null;

        try {
            const snapshot = JSON.parse(snapshotJSON);
            
            return (
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Collection Point Snapshot
                        </Typography>
                        <Button
                            size="small"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => handleCopyToClipboard(snapshotJSON)}
                        >
                            Copy JSON
                        </Button>
                    </Box>
                    
                    {/* Complete JSON Section */}
                    <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">Complete JSON Structure</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Editor
                                height="400px"
                                language="json"
                                value={snapshotJSON}
                                options={monacoOptions}
                                theme="vs-dark"
                            />
                        </AccordionDetails>
                    </Accordion>

                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: 'text.primary' }}>
                        Organized Sections
                    </Typography>
                    
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">Collection Point Info</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Editor
                                height="200px"
                                language="json"
                                value={JSON.stringify(snapshot.collectionPoint, null, 2)}
                                options={monacoOptions}
                                theme="vs-dark"
                            />
                        </AccordionDetails>
                    </Accordion>

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">
                                Purposes ({snapshot.purposes.length})
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Editor
                                height="400px"
                                language="json"
                                value={JSON.stringify(snapshot.purposes, null, 2)}
                                options={monacoOptions}
                                theme="vs-dark"
                            />
                        </AccordionDetails>
                    </Accordion>

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">
                                Data Elements ({snapshot.dataElements.length})
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Editor
                                height="300px"
                                language="json"
                                value={JSON.stringify(snapshot.dataElements, null, 2)}
                                options={monacoOptions}
                                theme="vs-dark"
                            />
                        </AccordionDetails>
                    </Accordion>

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">
                                Preferences ({snapshot.preferences.length})
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Editor
                                height="300px"
                                language="json"
                                value={JSON.stringify(snapshot.preferences, null, 2)}
                                options={monacoOptions}
                                theme="vs-dark"
                            />
                        </AccordionDetails>
                    </Accordion>

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">
                                Labels ({snapshot.labels.length})
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Editor
                                height="250px"
                                language="json"
                                value={JSON.stringify(snapshot.labels, null, 2)}
                                options={monacoOptions}
                                theme="vs-dark"
                            />
                        </AccordionDetails>
                    </Accordion>

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">Metadata</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Editor
                                height="150px"
                                language="json"
                                value={JSON.stringify(snapshot.metadata, null, 2)}
                                options={monacoOptions}
                                theme="vs-dark"
                            />
                        </AccordionDetails>
                    </Accordion>
                </Box>
            );
        } catch (error) {
            return (
                <Alert severity="error">
                    Failed to parse snapshot JSON for display
                </Alert>
            );
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* API Endpoints Section */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                        API Endpoints
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        Use these endpoints to interact with this collection point.
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Production Consent Submission
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Chip label="POST" size="small" color="primary" />
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', backgroundColor: 'action.hover', px: 1, py: 0.5, borderRadius: 1 }}>
                                {config.api.baseUrl}/collectionpoints/{collectionPoint?.CollectionPointIdentifier}/consent
                            </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Submit production consent records
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Sandbox Consent Submission
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Chip label="POST" size="small" color="secondary" />
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', backgroundColor: 'action.hover', px: 1, py: 0.5, borderRadius: 1 }}>
                                {config.api.baseUrl}/collectionpoints/{collectionPoint?.CollectionPointIdentifier}/consent/sandbox
                            </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Submit test consent records (marked as test data)
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Snapshot Retrieval
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Chip label="GET" size="small" color="info" />
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', backgroundColor: 'action.hover', px: 1, py: 0.5, borderRadius: 1 }}>
                                {config.api.baseUrl}/collectionpoints/{collectionPoint?.CollectionPointIdentifier}/detailed
                            </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Get the complete snapshot structure with translations and keys
                        </Typography>
                    </Box>
                </CardContent>
            </Card>

            {/* Snapshot JSON Section */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                        Snapshot JSON
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        This is the complete snapshot JSON structure for this collection point.
                    </Typography>
                    
                    {isLoadingSnapshot ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    ) : snapshotJSON ? (
                        renderCollapsibleSnapshot()
                    ) : (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            No snapshot data available for this collection point.
                        </Alert>
                    )}
                </CardContent>
            </Card>

            {/* Sample Payload Section */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                        Sample Consent Payload
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        Edit this sample payload and test it against the sandbox endpoint. The sample data will be generated in the selected language.
                    </Typography>
                    
                    {/* Language Selector */}
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            Sample Language:
                        </Typography>
                        <select
                            value={selectedLanguage}
                            onChange={(e) => setSelectedLanguage(e.target.value)}
                            style={{
                                padding: '8px 12px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                fontSize: '14px',
                                minWidth: '120px'
                            }}
                        >
                            <option value="en">English</option>
                            <option value="hi">Hindi</option>
                            <option value="bn">Bengali</option>
                            <option value="te">Telugu</option>
                            <option value="mr">Marathi</option>
                            <option value="ta">Tamil</option>
                            <option value="ur">Urdu</option>
                            <option value="gu">Gujarati</option>
                            <option value="kn">Kannada</option>
                            <option value="ml">Malayalam</option>
                            <option value="or">Odia</option>
                            <option value="pa">Punjabi</option>
                            <option value="as">Assamese</option>
                            <option value="mai">Maithili</option>
                            <option value="sat">Santali</option>
                            <option value="ks">Kashmiri</option>
                            <option value="ne">Nepali</option>
                            <option value="sd">Sindhi</option>
                            <option value="doi">Dogri</option>
                            <option value="kok">Konkani</option>
                            <option value="mni">Manipuri</option>
                            <option value="sa">Sanskrit</option>
                            <option value="bo">Bodo</option>
                        </select>
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                        <Editor
                            height="300px"
                            language="json"
                            value={samplePayload}
                            onChange={(value) => setSamplePayload(value || '')}
                            options={editableMonacoOptions}
                            theme="vs-dark"
                        />
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={<PlayArrowIcon />}
                            onClick={handleTestSubmission}
                            disabled={!samplePayload || isSubmittingTest}
                        >
                            {isSubmittingTest ? 'Submitting...' : 'Test Payload'}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => handleCopyToClipboard(samplePayload)}
                        >
                            Copy Payload
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Test Response Section */}
            {testResponse && (
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ color: 'text.primary' }}>
                                Test Response
                            </Typography>
                            <Button
                                size="small"
                                startIcon={<ContentCopyIcon />}
                                onClick={() => handleCopyToClipboard(testResponse)}
                            >
                                Copy Response
                            </Button>
                        </Box>
                        <Editor
                            height="200px"
                            language="json"
                            value={testResponse}
                            options={monacoOptions}
                            theme="vs-dark"
                        />
                    </CardContent>
                </Card>
            )}

            {/* Request Details Section */}
            {testRequestDetails && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                            Request Details
                        </Typography>
                        
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                URL
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', backgroundColor: 'action.hover', px: 1, py: 0.5, borderRadius: 1 }}>
                                {testRequestDetails.method} {testRequestDetails.url}
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Headers
                            </Typography>
                            <Editor
                                height="150px"
                                language="json"
                                value={JSON.stringify(testRequestDetails.headers, null, 2)}
                                options={monacoOptions}
                                theme="vs-dark"
                            />
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Request Body
                            </Typography>
                            <Editor
                                height="200px"
                                language="json"
                                value={testRequestDetails.body}
                                options={monacoOptions}
                                theme="vs-dark"
                            />
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Response Details Section */}
            {testResponseDetails && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                            Response Details
                        </Typography>
                        
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Status
                            </Typography>
                            <Chip 
                                label={`${testResponseDetails.status} ${testResponseDetails.statusText}`}
                                color={testResponseDetails.status >= 200 && testResponseDetails.status < 300 ? 'success' : 'error'}
                                size="small"
                            />
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Response Headers
                            </Typography>
                            <Editor
                                height="150px"
                                language="json"
                                value={JSON.stringify(testResponseDetails.headers, null, 2)}
                                options={monacoOptions}
                                theme="vs-dark"
                            />
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Response Body
                            </Typography>
                            <Editor
                                height="200px"
                                language="json"
                                value={testResponseDetails.body}
                                options={monacoOptions}
                                theme="vs-dark"
                            />
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Copy Notification */}
            <Snackbar
                open={showCopyNotification}
                autoHideDuration={config.ui.snackbar.autoHideDuration}
                onClose={() => setShowCopyNotification(false)}
                message="JSON copied to clipboard"
                anchorOrigin={config.ui.snackbar.anchorOrigin}
            />

            {/* Error Notification */}
            <Snackbar
                open={showErrorNotification}
                autoHideDuration={config.ui.snackbar.autoHideDuration}
                onClose={() => setShowErrorNotification(false)}
                anchorOrigin={config.ui.snackbar.anchorOrigin}
            >
                <Alert 
                    onClose={() => setShowErrorNotification(false)} 
                    severity="error" 
                    sx={{ width: '100%' }}
                >
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default IntegrationsTab;
